import { useMemo } from 'react';
import { PATHS, NAV_BAR_ROUTES } from 'src/constants/config';
import { useDecodedToken } from 'src/hooks/useDecodedToken';

// Move this outside the component to prevent recreation on each render
const flattenPaths = (paths) => {
  const flattened = {};

  Object.entries(paths).forEach(([key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      if ('value' in value) {
        flattened[key] = value;
      } else {
        Object.entries(value).forEach(([subKey, subValue]) => {
          flattened[`${key}_${subKey}`] = subValue;
        });
      }
    }
  });

  return flattened;
};

// Memoize the flattened paths since PATHS is a constant
const FLATTENED_PATHS = flattenPaths(PATHS);

// Memoize the route configurations since they're constant
const STAFF_ROUTES = {
  topRoutes: NAV_BAR_ROUTES.TOP_ROUTES,
  bottomRoutes: NAV_BAR_ROUTES.BOTTOM_ROUTES,
  hiddenRoutes: NAV_BAR_ROUTES.HIDDEN_ROUTES || [],
};

const EMPTY_ROUTES = {
  topRoutes: [],
  bottomRoutes: [],
  hiddenRoutes: [],
};

export const useRouteAccess = () => {
  const { decodedToken } = useDecodedToken();
  const userType = decodedToken?.userType;

  return useMemo(() => {
    // Always allow auth routes
    const isAuthRoute = (path) => path.startsWith('/auth/');

    if (typeof userType !== 'number') {
      return {
        isPathAllowed: (path) => isAuthRoute(path),
        filteredRoutes: EMPTY_ROUTES,
        userType,
      };
    }

    // For staff (userType 0), return all routes
    if (userType === 0) {
      return {
        isPathAllowed: () => true,
        filteredRoutes: STAFF_ROUTES,
        userType,
      };
    }

    // Calculate allowed paths for non-staff users
    const allowedPaths = Object.values(FLATTENED_PATHS)
      .filter((path) => !path.accessibleBy || path.accessibleBy.includes(userType))
      .map((path) => path.value);

    const filterRoutesByAccess = (routes) => {
      return routes.filter((route) => {
        const pathObj = Object.values(FLATTENED_PATHS).find((p) => p.value === route.path);
        return pathObj && (!pathObj.accessibleBy || pathObj.accessibleBy.includes(userType));
      });
    };

    const filteredRoutes = {
      topRoutes: filterRoutesByAccess(NAV_BAR_ROUTES.TOP_ROUTES),
      bottomRoutes: filterRoutesByAccess(NAV_BAR_ROUTES.BOTTOM_ROUTES),
      hiddenRoutes: filterRoutesByAccess(NAV_BAR_ROUTES.HIDDEN_ROUTES || []),
    };

    const isPathAllowed = (currentPath) => {
      if (isAuthRoute(currentPath)) return true;

      return allowedPaths.some((route) => {
        if (route.includes(':')) {
          const basePath = route.split(':')[0];
          return currentPath.startsWith(basePath);
        }
        return currentPath === route;
      });
    };

    return {
      isPathAllowed,
      filteredRoutes,
      userType,
    };
  }, [userType]);
};
